import { useCollection } from '@amzn/awsui-collection-hooks';
import {
	Box,
	CollectionPreferences,
	Header,
	Pagination,
	SpaceBetween,
	Table,
	TextFilter,
} from '@amzn/awsui-components-console';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { durationToString, msBetweenDates } from '../../commons';
import { compareBy, headerCounter } from '../../commons/componentHelpers';
import { CustomStatusIndicator, EmptyState, RefreshButton, ToggleableDatetime } from '../helpers';
import PAGES from '../../nav/Pages';
import InstanceOverview from '../../interfaces/instanceInterfaces';
import { useGroupName } from '../../data/redux';
import InstanceDateRangePicker, { dateRangeToStartAndEndDatetimes } from './InstanceDateRangePicker';
import InstanceStatusIdSelect from './InstanceStatusIdSelect';
import RerunExecutionInstanceButton from './RerunExecutionInstanceButton';
import InstanceBulkActionDropdown from './InstanceBulkActionDropdown';
import { fetchInstancesByActivity } from '../../data/api/fetchFromAPI';
import InternalLink from '../helpers/InternalLink';

// Due to a Polaris "quirk", all comparator functions must be declared OUTSIDE of a component in order to work
// Sage Post: https://refresh.sage.amazon.dev/posts/1371292#1371320
const startComparator = compareBy<InstanceOverview>('startDateTime', msBetweenDates);
const endComparator = compareBy<InstanceOverview>('endDateTime', msBetweenDates);
const scheduleComparator = compareBy<InstanceOverview>('scheduleDateTime', msBetweenDates);

/**
 * A reusable table component for displaying instance data with filtering, sorting, and bulk actions.
 * Can be used in both activity-level and pipeline-level contexts.
 */
export default function MonitorInstanceTable(props: {
	instances: InstanceOverview[];
	loading: boolean;
	showActivityInfo: boolean;
	pipelineName?: string;
	onRefresh: () => void;
}) {
	const groupName = useGroupName();

	// URL parameters for filtering
	const [searchParams] = useSearchParams();
	const dateRangeParam = searchParams.get('dateRange');
	const dateRange = dateRangeParam ? JSON.parse(dateRangeParam) : {};
	const instanceStatusId = parseInt(searchParams.get('instanceStatusId') || '-1') || -1;

	// internal state for table for pagination and bulk select
	const [selectedInstances, setSelectedInstances] = useState<InstanceOverview[]>([]);
	// default to 25 items per page for pipeline context, 10 for activity context; pipeline will have more instances
	const [preferences, setPreferences] = useState({ pageSize: props.showActivityInfo ? 25 : 10 } as any);

	const headerTitle = props.showActivityInfo ? 'Pipeline Instances' : 'Activity Instances';
	const [startScheduleDateTime, endScheduleDateTime] = dateRangeToStartAndEndDatetimes(dateRange);

	// Clear selections when data refetch occurs (date range or status filter changes)
	useEffect(() => {
		setSelectedInstances([]);
	}, [dateRangeParam, instanceStatusId]);

	// A 'useful' Polaris hook for defining table properties: https://polaris.a2z.com/get-started/dev-guides/collection-hooks/
	const { items, collectionProps, paginationProps, filterProps } = useCollection(
		[...props.instances].sort((a, b) => msBetweenDates(b.scheduleDateTime, a.scheduleDateTime)),
		{
			filtering: {
				empty: (
					<EmptyState
						title="No instances"
						subtitle="There were no instances found within the given dates for this activity."
					/>
				),
				noMatch: <EmptyState title="No matches" subtitle={`No instances matched the filtering string.`} />,
			},
			pagination: { pageSize: preferences.pageSize },
			sorting: {},
			selection: {
				keepSelection: true,
				trackBy: 'instanceId',
			},
		}
	);

	// Wrap onRefresh to clear selections when refresh is triggered
	const handleRefresh = () => {
		setSelectedInstances([]);
		props.onRefresh();
	};

	// header for table with filtering function
	const FilteringTableHeader = (
		<SpaceBetween size="xs">
			<Header
				actions={
					<SpaceBetween size="xs" direction="horizontal">
						<RefreshButton refreshing={props.loading} onRefresh={handleRefresh} />
						<InstanceBulkActionDropdown
							selectedInstances={selectedInstances}
							setSelectedInstances={setSelectedInstances}
							isRefreshing={props.loading}
						/>
					</SpaceBetween>
				}
				counter={headerCounter(selectedInstances.length, props.instances.length)}
				variant="h3"
			>
				{headerTitle}
			</Header>
			<SpaceBetween size="s" direction="horizontal">
				<Box padding={{ top: 'xxs' }}>Fetch instances from </Box>
				<InstanceDateRangePicker />
				{!props.showActivityInfo && (
					<>
						<Box padding={{ top: 'xxs' }}> with status </Box>
						<InstanceStatusIdSelect />
					</>
				)}
			</SpaceBetween>
		</SpaceBetween>
	);

	const columnDefinitions = [
		// conditional activity columns, these will be hidden when viewing within an activity since it's redundant
		...(props.showActivityInfo
			? [
					{
						id: 'activityId',
						header: 'Activity ID',
						cell: ({ activityId }: InstanceOverview) => (
							<InternalLink href={PAGES.VIEW_ACTIVITY.path} pathParams={{ activityId }} groupName>
								{activityId}
							</InternalLink>
						),
						sortingField: 'activityId',
					},
					{
						id: 'activityName',
						header: 'Activity Name',
						cell: (item: InstanceOverview) => item.activityName,
						sortingField: 'activityName',
					},
			  ]
			: []),
		// standard instance columns that will be shown in all cases
		{
			id: 'id',
			header: 'ID',
			cell: ({ activityId, instanceId }: InstanceOverview) => (
				<InternalLink href={PAGES.VIEW_INSTANCE.path} pathParams={{ activityId, instanceId }} groupName>
					{instanceId}
				</InternalLink>
			),
			sortingField: 'instanceId',
		},
		{
			id: 'status',
			header: 'Status',
			cell: (item: InstanceOverview) => <CustomStatusIndicator>{item.status}</CustomStatusIndicator>,
			sortingField: 'status',
		},
		{
			id: 'retry',
			header: 'Retry',
			cell: (item: InstanceOverview) => (
				<RerunExecutionInstanceButton
					instanceId={item.instanceId}
					activityId={item.activityId.toString()}
					pipelineName={props.pipelineName}
					variant="inline-icon"
					onSuccess={() => {
						fetchInstancesByActivity(
							{
								activityId: item.activityId,
								groupName,
								startScheduleDateTime,
								endScheduleDateTime,
								instanceStatusId,
							},
							true
						);
					}}
				/>
			),
		},
		{
			id: 'schedule',
			header: 'Schedule',
			cell: (item: InstanceOverview) => <ToggleableDatetime>{item.scheduleDateTime}</ToggleableDatetime>,
			sortingComparator: scheduleComparator,
		},
		{
			id: 'start',
			header: 'Start',
			cell: (item: InstanceOverview) => <ToggleableDatetime>{item.startDateTime}</ToggleableDatetime>,
			sortingComparator: startComparator,
		},
		{
			id: 'end',
			header: 'End',
			cell: (item: InstanceOverview) => <ToggleableDatetime>{item.endDateTime}</ToggleableDatetime>,
			sortingComparator: endComparator,
		},
		{
			id: 'duration',
			header: 'Duration',
			cell: (item: InstanceOverview) => durationToString(item.duration, { granularity: 2 }),
			sortingField: 'duration',
		},
		{
			id: 'retries',
			header: 'Retry count',
			cell: (item: InstanceOverview) => item.retryCount,
			sortingField: 'retryCount',
		},
	];

	return (
		<Table
			{...collectionProps}
			data-testid="monitorInstanceTable"
			loading={props.loading}
			loadingText="Loading instances"
			header={FilteringTableHeader}
			items={items}
			selectionType="multi"
			selectedItems={selectedInstances}
			onSelectionChange={({ detail }) => {
				setSelectedInstances(detail.selectedItems);
			}}
			columnDefinitions={columnDefinitions}
			stickyColumns={{ first: 1 }}
			resizableColumns={props.showActivityInfo}
			filter={
				<TextFilter
					data-testid="monitorInstanceTable_textFilter"
					filteringAriaLabel="Filter instances"
					filteringPlaceholder="Filter instances by properties"
					{...filterProps}
				/>
			}
			pagination={
				<Pagination
					data-testid="monitorInstanceTable_pagination"
					{...paginationProps}
					ariaLabels={{
						nextPageLabel: 'Next page',
						previousPageLabel: 'Previous page',
						pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
					}}
				/>
			}
			preferences={
				<CollectionPreferences
					title="Preferences"
					confirmLabel="Confirm"
					cancelLabel="Cancel"
					data-testid="monitorInstanceTable_preferences"
					pageSizePreference={{
						title: 'Page size',
						options: [
							{ value: 10, label: '10 instances' },
							{ value: 25, label: '25 instances' },
							{ value: 50, label: '50 instances' },
						],
					}}
					wrapLinesPreference={{
						label: 'Wrap lines',
						description: 'Select to see all the text within each cell by wrapping lines',
					}}
					preferences={preferences}
					onConfirm={({ detail }) => {
						setPreferences(detail);
					}}
				/>
			}
		/>
	);
}
